import {
  METRICS,
  METRIC_KEYS,
  CHANNEL_LABELS,
  COMPARE_MODES,
  SOURCE_COLORS,
  DEFAULT_SOURCE_COLOR,
  MONTH_OPTIONS,
  PAGE_SERIES_COLORS,
  TOP_PAGES_COUNT,
  scopedCountryCodes,
} from './constants';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// 'YYYY-MM-DD' for the API (date-only, no timezone shift).
export const formatApiDate = date => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// '2026-06' -> 'Jun-26'
// '20 Jul 2026' — compact display for the range-picker field.
export const formatDisplayDate = date =>
  date
    ? new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

export const formatMonthLabel = month => {
  if (!month || month.indexOf('-') === -1) return month || '';
  const [year, mon] = month.split('-');
  const idx = parseInt(mon, 10) - 1;
  return `${MONTH_NAMES[idx] || mon}-${year.slice(2)}`;
};

const lastDayOfMonth = date =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const isMonthEnd = date => date.getDate() === lastDayOfMonth(date);

// Month shift without JS date overflow (Mar 31 - 1 month must NOT become Mar 3):
// clamp the day to the target month's length.
const addMonthsClamped = (date, delta) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + delta);
  d.setDate(Math.min(day, lastDayOfMonth(d)));
  return d;
};

// Shift a window by whole months, keeping calendar alignment: if the window
// ends on a month-end, the shifted window also ends on ITS month-end — so
// "June 1-30 vs previous month" compares against the FULL May (1-31), exactly
// what filtering May directly would query.
const shiftWindowByMonths = (start, end, delta) => {
  const newStart = addMonthsClamped(start, delta);
  const newEnd = addMonthsClamped(end, delta);
  if (isMonthEnd(end)) newEnd.setDate(lastDayOfMonth(newEnd));
  return { startDate: newStart, endDate: newEnd };
};

const addDays = (date, delta) => {
  const d = new Date(date);
  d.setDate(d.getDate() + delta);
  return d;
};

// Derive the comparison window {startDate, endDate} (Date objects) from the
// primary window and the selected compare mode. Returns null when no comparison.
// The comparison runs the SAME query with only the dates swapped, so its numbers
// always match filtering that range directly.
export const computeCompareWindow = (
  startDate,
  endDate,
  mode,
  customStart,
  customEnd,
) => {
  if (!startDate || !endDate || !mode || mode === COMPARE_MODES.NONE) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  switch (mode) {
    case COMPARE_MODES.PREV_MONTH:
      return shiftWindowByMonths(start, end, -1);
    case COMPARE_MODES.PREV_YEAR:
      return shiftWindowByMonths(start, end, -12);
    case COMPARE_MODES.PREV_PERIOD: {
      const lengthDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
      const prevEnd = addDays(start, -1);
      const prevStart = addDays(prevEnd, -lengthDays);
      return { startDate: prevStart, endDate: prevEnd };
    }
    case COMPARE_MODES.CUSTOM:
      return customStart && customEnd
        ? { startDate: new Date(customStart), endDate: new Date(customEnd) }
        : null;
    default:
      return null;
  }
};

const ZERO_TOTALS = () =>
  METRIC_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

// A response payload coerced to an array (funnel hooks return [] on
// empty/failure, but guard anyway). Shared by every tab.
export const asRows = data => (Array.isArray(data) ? data : []);

// Extract the four funnel metrics from a row as numbers (0 when absent).
export const metricFields = row =>
  METRIC_KEYS.reduce((acc, key) => {
    acc[key] = Number(row[key] || 0);
    return acc;
  }, {});

// Sum a list of funnel rows into a single totals object.
export const sumRows = (rows = []) =>
  rows.reduce((acc, row) => {
    METRIC_KEYS.forEach(key => {
      acc[key] += Number(row[key] || 0);
    });
    return acc;
  }, ZERO_TOTALS());

// Conversion percentage (0-100, one decimal) or null when denominator is 0.
export const conversionPct = (numerator, denominator) => {
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 1000) / 10;
};

// Percent change current-vs-previous, or null when previous is 0/absent.
export const deltaPct = (current, previous) => {
  if (previous === null || previous === undefined || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
};

// Pivot timeseries rows [{month, channel, ql, ...}] into chart-friendly shape.
// Channel list is derived from the DATA (whatever channels the DB returned),
// never from a hardcoded list.
export const pivotTimeseries = (rows = []) => {
  const monthSet = [];
  const channelSet = [];
  rows.forEach(row => {
    if (monthSet.indexOf(row.month) === -1) monthSet.push(row.month);
    if (channelSet.indexOf(row.channel) === -1) channelSet.push(row.channel);
  });
  monthSet.sort();
  channelSet.sort();

  const monthIndex = {};
  monthSet.forEach((m, i) => {
    monthIndex[m] = i;
  });

  const metricKeys = METRIC_KEYS;
  const series = {};
  channelSet.forEach(channel => {
    series[channel] = {};
    metricKeys.forEach(metric => {
      series[channel][metric] = new Array(monthSet.length).fill(0);
    });
  });

  rows.forEach(row => {
    const chan = series[row.channel];
    const idx = monthIndex[row.month];
    if (!chan || idx === undefined) return;
    metricKeys.forEach(metric => {
      chan[metric][idx] = Number(row[metric] || 0);
    });
  });

  return { months: monthSet, channels: channelSet, series };
};

// Sum a metric across all channels per month -> flat array aligned to months.
export const totalPerMonth = (pivot, metric) =>
  pivot.months.map((_, i) =>
    pivot.channels.reduce(
      (sum, channel) =>
        sum + (pivot.series[channel][metric] ? pivot.series[channel][metric][i] : 0),
      0,
    ),
  );

export const channelLabel = channel => CHANNEL_LABELS[channel] || channel;

export const channelColor = channel => SOURCE_COLORS[channel] || DEFAULT_SOURCE_COLOR;

// Collapse every homepage variant (/, /en-in/, /en-us/, /usa/, /#..., /?...)
// into a single 'Homepage' entity — mirrors web-utils' removeHrefLangFromPath +
// BRAND homepage list. Used for the top-pages GRAPH only, so locale homepages
// don't crowd out real pages.
export const normalizeHomepage = page => {
  if (!page || page === '(unknown)') return page;
  const withoutLocale = page.replace(/^\/[a-z]{2}-[a-z]{2,4}(\/|$)/i, '/');
  if (
    withoutLocale === '/' ||
    withoutLocale === '' ||
    withoutLocale === '/usa/' ||
    withoutLocale.indexOf('/#') === 0 ||
    withoutLocale.indexOf('/?') === 0
  ) {
    return 'Homepage';
  }
  return page;
};

// Build the query object for a funnel endpoint from the current filter state.
// `channelOverride` locks the category (category tabs); `windowOverride` swaps in
// the comparison window. Empty multi-selects are omitted (=> "all") so the URL
// stays clean.
export const buildFunnelQuery = (filters, channelOverride, windowOverride) => {
  const startDate = windowOverride ? windowOverride.startDate : filters.startDate;
  const endDate = windowOverride ? windowOverride.endDate : filters.endDate;
  const query = {
    start_date: formatApiDate(startDate),
    end_date: formatApiDate(endDate),
  };
  if (channelOverride) {
    query.channels = [channelOverride];
  } else if (filters.channels && filters.channels.length) {
    query.channels = filters.channels;
  }
  // Country supersedes region: when countries (scoped to the selected regions)
  // are chosen we filter by country_code and drop the region_bucket filter —
  // the two are different data lineages, so ANDing them would silently exclude
  // legitimately-mismatched leads. No countries => region filter as before.
  const countryCodes = scopedCountryCodes(filters.regions, filters.countries);
  if (countryCodes.length) query.countries = countryCodes;
  else if (filters.regions && filters.regions.length) query.regions = filters.regions;
  if (filters.userTypes && filters.userTypes.length)
    query.is_old_user = filters.userTypes;
  if (filters.subSources && filters.subSources.length)
    query.sub_sources = filters.subSources;
  // NOTE: `experiment` is deliberately NOT added here. It must only reach the
  // /experiment/ variant-breakdown endpoint (the Experiment Analysis tab sends
  // it via buildRangeParams) — in the base query it would silently narrow the
  // headline KPIs/timeseries to experiment-tagged leads only. KPIs stay
  // category-wide.
  return query;
};

// True when both ends of the primary window are set (required for any fetch).
export const hasValidWindow = filters => !!(filters.startDate && filters.endDate);

// Base funnel query params from a date range + optional dimensions. Shared by
// the chip-driven tabs (Category/Non-Content) and Page Analysis. Empty
// multi-selects are omitted (=> "all") so URLs/keys stay clean; only the keys a
// caller passes appear, so the produced key matches each tab's prior hand-built
// query (no spurious refetch).
export const buildRangeParams = (
  startDate,
  endDate,
  { channel, regions = [], countries = [], userTypes = [], analyzePath, experiment } = {},
) => {
  const query = {
    start_date: formatApiDate(startDate),
    end_date: formatApiDate(endDate),
  };
  if (channel) query.channels = [channel];
  if (analyzePath) query.analyze_path = analyzePath;
  if (experiment && experiment.trim()) query.experiment = experiment.trim();
  if (userTypes.length) query.is_old_user = userTypes;
  // Country supersedes region (see buildFunnelQuery): filter by country_code and
  // drop region_bucket when countries scoped to the selected regions are chosen.
  const countryCodes = scopedCountryCodes(regions, countries);
  if (countryCodes.length) query.countries = countryCodes;
  else if (regions.length) query.regions = regions;
  return query;
};

// Build stacked-bar datasets for a month-on-month graph: rank entities by total
// QL over the year, keep the top TOP_PAGES_COUNT, and plot `metric` per month.
// `entityOf(row)` maps a row to its series label (e.g. normalized page, or a
// sub-source pair label) — the only thing that differs between Category and
// Non-Content graphs. Returns the datasets (empty array => no data).
export const buildMonthlyTopSeries = (rows = [], graphYear, metric, entityOf) => {
  const monthKeys = MONTH_OPTIONS.map(
    month => `${graphYear}-${String(month.value + 1).padStart(2, '0')}`,
  );
  const byMonthEntity = {};
  const qlByEntity = {};
  rows.forEach(row => {
    const entity = entityOf(row);
    const monthEntityKey = `${row.month}|${entity}`;
    byMonthEntity[monthEntityKey] =
      (byMonthEntity[monthEntityKey] || 0) + Number(row[metric] || 0);
    qlByEntity[entity] = (qlByEntity[entity] || 0) + Number(row[METRICS.QL] || 0);
  });
  const topEntities = Object.keys(qlByEntity)
    .sort((a, b) => qlByEntity[b] - qlByEntity[a])
    .slice(0, TOP_PAGES_COUNT);
  return topEntities.map((entity, index) => ({
    label: entity,
    color: PAGE_SERIES_COLORS[index % PAGE_SERIES_COLORS.length],
    data: monthKeys.map(monthKey => byMonthEntity[`${monthKey}|${entity}`] || 0),
  }));
};

// Format the funnel's last-rebuild timestamp (UTC ISO like "2026-07-24T09:51:12Z",
// from data_playground.mbt_* relcreationtime) for display in IST — e.g.
// "24 Jul 2026, 3:21 PM IST". Returns '' when missing/unparseable so the caller
// can simply omit the note.
export const formatRefreshedIST = isoUtc => {
  if (!isoUtc) return '';
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const part = type => (parts.find(p => p.type === type) || {}).value || '';
  return (
    `${part('day')} ${part('month')} ${part('year')}, ` +
    `${part('hour')}:${part('minute')} ${part('dayPeriod').toUpperCase()} IST`
  );
};
